import { useEffect, useRef, useState } from 'react';
import API from '@/lib/api';

const rectStyle = r => ({ left:`${r.x*100}%`, top:`${r.y*100}%`, width:`${r.w*100}%`, height:`${r.h*100}%` });
export const DocumentTextOverlay = ({ docId, pageNumber, active, onSelect, selected }) => {
  const ref = useRef(null);
  const start = useRef(null);
  const loadedKey = useRef('');
  const [visible,setVisible] = useState(false);
  const [data,setData] = useState(null);
  const [loading,setLoading] = useState(false);
  const [error,setError] = useState('');
  const [ocr,setOcr] = useState(false);
  const [retry,setRetry] = useState(0);
  const [drag,setDrag] = useState(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), {rootMargin:'80px'});
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!active || !visible) return;
    const key=`${docId}:${pageNumber}:${ocr}:${retry}`;
    if(loadedKey.current===key)return;
    let alive = true;
    setLoading(true); setError(''); setData(null);
    API.get(`/inkomende-documenten/${docId}/tekstlaag`, {params:{pagina:pageNumber,ocr},timeout:90000})
      .then(r => { if(alive){setData(r.data);loadedKey.current=key;} })
      .catch(e => { if(alive) setError(e.response?.data?.detail || 'Tekst lezen mislukt'); })
      .finally(() => { if(alive) setLoading(false); });
    return () => { alive=false; };
  },[docId,pageNumber,ocr,retry,!!active,visible]);
  const point = e => { const r=ref.current.getBoundingClientRect(); return {x:Math.min(1,Math.max(0,(e.clientX-r.left)/r.width)), y:Math.min(1,Math.max(0,(e.clientY-r.top)/r.height))}; };
  const choose = words => {
    if (!words.length) { setError('Geen tekst in deze selectie. Probeer een groter kader of lees als scan.'); return; }
    const lines = new Map(); words.forEach(w => lines.set(w.regel,[...(lines.get(w.regel)||[]),w.text]));
    const scores=words.map(w=>w.confidence).filter(v=>v!==null);
    onSelect({pagina:pageNumber,woord_ids:words.map(w=>w.id),document_hash:data.document_hash,ocr,
      text:[...lines.values()].map(w=>w.join(' ')).join('\n'), bron:data.bron, confidence:scores.length?Math.min(...scores):null,
      vak:{x:Math.min(...words.map(w=>w.x)),y:Math.min(...words.map(w=>w.y)),rechts:Math.max(...words.map(w=>w.x+w.w)),onder:Math.max(...words.map(w=>w.y+w.h))}});
  };
  const end = e => {
    if (!start.current || !data) return;
    e.preventDefault();
    const p=point(e),s=start.current; start.current=null; setDrag(null);
    if(ref.current.hasPointerCapture(e.pointerId))ref.current.releasePointerCapture(e.pointerId);
    const r={x:Math.min(s.x,p.x),y:Math.min(s.y,p.y),w:Math.abs(s.x-p.x),h:Math.abs(s.y-p.y)};
    const size=ref.current.getBoundingClientRect();
    const click=r.w*size.width<5 && r.h*size.height<5;
    // Natural horizontal text dragging has almost no height. Include the touched word's
    // line band, rather than demanding that word centres fall inside a zero-height rectangle.
    const first=data.woorden.find(w=>String(w.id)===s.wordId);
    const horizontal=r.h*size.height<6;
    const top=horizontal && first ? first.y : r.y;
    const bottom=horizontal && first ? first.y+first.h : r.y+r.h;
    const padX=2/size.width,padY=2/size.height;
    choose(data.woorden.filter(w=>click
      ? (first ? w.id===first.id : p.x>=w.x && p.x<=w.x+w.w && p.y>=w.y && p.y<=w.y+w.h)
      : w.x+w.w>=r.x-padX && w.x<=r.x+r.w+padX && w.y+w.h/2>=top-padY && w.y+w.h/2<=bottom+padY));
  };
  const mark=selected?.pagina===pageNumber ? selected.vak : null;
  return <div ref={ref} className={`absolute inset-0 z-10 select-none ${active?'cursor-crosshair':'pointer-events-none'}`} style={{touchAction:active?'none':'auto',userSelect:'none',WebkitUserSelect:'none'}} data-testid={`text-overlay-${pageNumber}`}
    onPointerDown={e=>{if(!active||!data||loading||e.button!==0||e.target.closest('[data-source-control]'))return; e.preventDefault();start.current={...point(e),wordId:e.target.closest('[data-source-word]')?.dataset.sourceWord};ref.current.setPointerCapture(e.pointerId);setError('');}}
    onPointerMove={e=>{if(start.current){const p=point(e),s=start.current;setDrag({x:Math.min(s.x,p.x),y:Math.min(s.y,p.y),w:Math.abs(p.x-s.x),h:Math.abs(p.y-s.y)});}}}
    onPointerUp={end} onPointerCancel={()=>{start.current=null;setDrag(null);}} onLostPointerCapture={()=>{start.current=null;setDrag(null);}}>
    {active && data?.woorden.map(w=><button key={w.id} type="button" aria-label={w.text} title={w.text} draggable={false} data-source-word={w.id} data-testid={`source-word-${pageNumber}-${w.id}`}
      onClick={e=>{if(e.detail===0)choose([w]);}} className="absolute border border-transparent hover:border-[#002FA7] hover:bg-blue-400/20 focus:bg-blue-400/30 focus:outline-none" style={rectStyle(w)} />)}
    {mark && <div className="absolute border-2 border-emerald-500 bg-emerald-300/20 pointer-events-none" style={rectStyle({x:mark.x,y:mark.y,w:mark.rechts-mark.x,h:mark.onder-mark.y})} data-testid={`source-highlight-${pageNumber}`} />}
    {drag && <div className="absolute border-2 border-[#002FA7] bg-blue-300/20 pointer-events-none" style={rectStyle(drag)} data-testid={`source-drag-${pageNumber}`} />}
    {active && <div data-source-control className="absolute left-1 bottom-1 right-1 flex gap-2 flex-wrap items-center pointer-events-auto text-[10px]" onPointerDown={e=>e.stopPropagation()}>
      {(loading||error||data?.woorden.length===0) && <span className="bg-white/95 px-2 py-1 text-slate-700 border border-slate-200" role="status" data-testid={`text-status-${pageNumber}`}>{loading?'Tekst wordt gelezen…':error||'Geen tekst herkend'}</span>}
      {!loading && <button type="button" className="ml-auto bg-white/95 border border-slate-300 px-2 py-1 text-[#002FA7] hover:bg-blue-50" onClick={()=>{setOcr(true);setRetry(n=>n+1);}} data-testid={`text-ocr-${pageNumber}`}>{ocr?'Opnieuw lezen':'Als scan lezen'}</button>}
    </div>}
  </div>;
};